import {Fade} from '@esgi/ui-kit/transition';
import {ReactNode} from 'react';
import {ArrowNavigation, ClickOutsideListener, FocusLock, useKeyboardEffect} from '@esgi/ui-kit/core';
import {DetailedProps, join, tryCall} from '@esgi/ui-kit/utils';

import styles from './menu.module.less';

export interface MenuProps {
	show: boolean;
	className?: string;
	disableAnimation?: boolean;
	keepFocusInside?: boolean;
	onEscPressed?: (e: KeyboardEvent) => void;
	onClickOutside?: (e: MouseEvent | TouchEvent) => void;
	children?: ReactNode;
}

type Props = MenuProps & Omit<DetailedProps<HTMLUListElement>, 'role' | 'aria-hidden'>;

export function Menu(props: Props): JSX.Element {
	const {
		show,
		onClickOutside,
		onEscPressed,
		disableAnimation,
		keepFocusInside,
		className,
		children,
		...ulProps
	} = props;
	useKeyboardEffect('keydown', (event) => event.key === 'Escape' && tryCall(onEscPressed), false);

	let out = <ArrowNavigation disable={!show}>
		<ul {...ulProps} className={join(className, styles.menu)} role='menu' aria-hidden={!show}>
			{children}
		</ul>
	</ArrowNavigation>;

	if (show) {
		if (onClickOutside) {
			out = <ClickOutsideListener active onClickedOutside={onClickOutside}>
				{out}
			</ClickOutsideListener>;
		}
		if (keepFocusInside) {
			out = <FocusLock>
				{out}
			</FocusLock>;
		}
	}

	if (!disableAnimation) {
		return <Fade in={show} duration='fast'>
			{out}
		</Fade>;
	} else {
		return out;
	}
}
